import { dark, light } from '@london-life/game/house/room';
import { BOOKS, BOTTLES, FRUIT, legs, row, type Draw } from './common';

// Set pieces for rooms: offices and study, food and drink, fun, shops, culture, health and sport.

const SCREEN = '#2f6fa3';

export const INDOOR: Record<string, Draw> = {
  // ---------- work and study
  reception(k, { w, d, h, c, label }, g) {
    k.box(w, h, d * 0.55, c, 0, h / 2, d * 0.2, g); // the front
    k.box(w + 0.06, 0.05, d * 0.6, light(c, 0.6), 0, h + 0.02, d * 0.2, g);
    k.box(w * 0.9, h * 0.7, d * 0.35, dark(c, 0.8), 0, (h * 0.7) / 2, -d * 0.25, g); // the desk behind
    k.box(0.45, 0.3, 0.04, '#1f2429', -w * 0.25, h * 0.7 + 0.2, -d * 0.3, g);
    k.lit(0.4, 0.24, 0.01, SCREEN, -w * 0.25, h * 0.7 + 0.2, -d * 0.275, g, 0.6);
    if (label) k.sign(label, 0, h * 0.55, d * 0.48, g, Math.min(w * 0.8, 1.8), '#fffdf5', dark(c, 0.6));
  },
  deskpod(k, { w, d, h, c }, g) {
    // four desks back to back, a screen and a chair at each
    k.box(w, 0.05, d, c, 0, h - 0.025, 0, g);
    legs(k, w, d, h - 0.05, dark(c, 0.5), g, 0.05);
    k.box(w * 0.98, 0.32, 0.03, '#d9dee1', 0, h + 0.16, 0, g); // the divider
    for (const side of [-1, 1])
      for (const across of [-1, 1]) {
        const x = (across * w) / 4,
          z = (side * d) / 4;
        k.box(0.5, 0.32, 0.03, '#1f2429', x, h + 0.22, z - side * 0.12, g);
        k.lit(0.45, 0.26, 0.01, SCREEN, x, h + 0.22, z - side * 0.12 + side * 0.017, g, 0.5);
        k.box(0.42, 0.05, 0.42, '#2b3238', x, 0.48, z + side * 0.45, g); // chair seat
        k.box(0.42, 0.45, 0.05, '#2b3238', x, 0.75, z + side * 0.66, g);
        k.cylinder(0.03, 0.45, '#5b636b', x, 0.23, z + side * 0.45, g);
      }
  },
  meetingroom(k, { w, d, h, c }, g) {
    // a glass box with a slim frame and a long table inside
    k.glass(w, h, 0.04, 0, h / 2, -d / 2, g);
    k.glass(0.04, h, d, -w / 2, h / 2, 0, g);
    k.glass(0.04, h, d, w / 2, h / 2, 0, g);
    k.glass(w * 0.55, h, 0.04, -w * 0.225, h / 2, d / 2, g); // front, with a doorway
    for (const [x, z] of [
      [-1, -1],
      [1, -1],
      [-1, 1],
      [1, 1],
    ] as const)
      k.box(0.06, h, 0.06, dark(c, 0.6), (x * w) / 2, h / 2, (z * d) / 2, g);
    for (const z of [-1, 1]) k.box(w, 0.06, 0.06, dark(c, 0.6), 0, h, (z * d) / 2, g);
    for (const x of [-1, 1]) k.box(0.06, 0.06, d, dark(c, 0.6), (x * w) / 2, h, 0, g);
    k.box(w * 0.55, 0.05, d * 0.4, c, 0, 0.74, 0, g);
    legs(k, w * 0.5, d * 0.35, 0.72, '#3d4650', g);
    for (const x of [-0.3, 0, 0.3])
      for (const z of [-1, 1]) k.box(0.35, 0.45, 0.35, '#3d4650', x * w, 0.23, z * d * 0.33, g);
    k.box(w * 0.4, h * 0.3, 0.03, '#1f2429', 0, h * 0.55, -d / 2 + 0.05, g);
    k.lit(w * 0.36, h * 0.25, 0.01, SCREEN, 0, h * 0.55, -d / 2 + 0.07, g, 0.5);
  },
  lifts(k, { w, h, c }, g) {
    // lift doors in the wall, with the floor numbers lit above
    const count = Math.max(1, Math.round(w / 1.4));
    k.box(w, h, 0.12, dark(c, 0.85), 0, h / 2, 0, g);
    for (let i = 0; i < count; i += 1) {
      const x = -w / 2 + (w * (i + 0.5)) / count;
      for (const side of [-1, 1]) k.box(0.42, 2.1, 0.04, '#c9cfd3', x + side * 0.22, 1.05, 0.07, g);
      k.box(0.02, 2.1, 0.05, '#7d8790', x, 1.05, 0.08, g);
      k.lit(0.36, 0.12, 0.02, '#f59e0b', x, 2.3, 0.08, g, 0.9);
      k.box(0.08, 0.14, 0.03, '#2b3238', x + 0.6, 1.2, 0.08, g);
    }
  },
  cooler(k, { h, c }, g) {
    k.box(0.32, h * 0.7, 0.32, c, 0, (h * 0.7) / 2, 0, g);
    k.cylinder(0.15, h * 0.3, '#9fd3f2', 0, h * 0.85, 0, g);
  },
  loos(k, { w, h, c }, g) {
    // two toilet doors in a wall: a man and a woman on the signs
    k.box(w, h, 0.12, light(c, 0.2), 0, h / 2, 0, g);
    for (const side of [-1, 1]) {
      k.box(0.8, 2.0, 0.04, c, side * w * 0.25, 1.0, 0.07, g);
      k.sphere(0.03, '#e9c46a', side * w * 0.25 - side * 0.3, 1.0, 0.1, g);
      k.box(0.18, 0.18, 0.02, side < 0 ? '#1d4ed8' : '#be185d', side * w * 0.25, 1.6, 0.1, g);
    }
  },
  lectern(k, { h, c }, g) {
    k.box(0.5, h * 0.9, 0.4, c, 0, (h * 0.9) / 2, 0, g);
    k.box(0.6, 0.05, 0.5, dark(c, 0.8), 0, h * 0.92, 0.03, g);
    k.cylinder(0.01, 0.3, '#2a2d31', 0.15, h + 0.1, -0.1, g);
  },
  seatrow(k, { w, d, h, c, variant }, g) {
    // rows of seats, each row a step higher (a lecture hall, an arena), or one row on the flat
    const rows = Math.max(1, Math.round(d / 0.85));
    const tiered = variant === 'tiered';
    const seats = Math.max(1, Math.round(w / 0.55));
    for (let r = 0; r < rows; r += 1) {
      const z = d / 2 - (d * (r + 0.5)) / rows;
      const rise = tiered ? r * 0.3 : 0;
      if (tiered) k.box(w, rise + 0.02, d / rows, dark(c, 0.55), 0, (rise + 0.02) / 2, z, g);
      for (let s = 0; s < seats; s += 1) {
        const x = -w / 2 + (w * (s + 0.5)) / seats;
        k.box(0.44, 0.08, 0.42, c, x, rise + h * 0.5, z + 0.05, g);
        k.box(0.44, h * 0.55, 0.06, dark(c, 0.8), x, rise + h * 0.75, z - 0.17, g);
        k.box(0.06, h * 0.5, 0.36, '#3d4650', x - 0.24, rise + h * 0.25, z, g);
      }
    }
  },
  bookcases(k, { w, d, h, c }, g) {
    k.box(w, h, d, c, 0, h / 2, 0, g);
    const shelves = Math.max(2, Math.round(h / 0.45));
    for (let s = 0; s < shelves; s += 1) {
      const y = 0.08 + (h - 0.1) * (s / shelves);
      k.box(w * 0.94, 0.03, d * 0.9, dark(c, 0.7), 0, y, 0.02, g);
      row(k, Math.round(w * 14), w * 0.9, y + 0.015, d / 2 - 0.08, g, BOOKS, { w: 0.05, h: 0.3, d: 0.18 });
    }
  },
  piano(k, { w, d, h, c }, g) {
    k.box(w, h, d * 0.45, c, 0, h / 2, -d * 0.27, g); // upright body
    k.box(w, 0.08, d * 0.4, dark(c, 0.8), 0, h * 0.55, d * 0.12, g); // keys shelf
    k.box(w * 0.94, 0.02, d * 0.22, '#fffdf5', 0, h * 0.59, d * 0.18, g); // keys
    row(k, Math.round(w * 9), w * 0.9, h * 0.6, d * 0.12, g, ['#111418'], { w: 0.03, h: 0.02, d: 0.1 });
    k.box(w * 0.5, 0.06, 0.4, dark(c, 0.6), 0, 0.45, d * 0.55, g); // the stool
  },

  // ---------- food and drink
  bar(k, { w, d, h, c, variant }, g) {
    // the counter at the front, a back bar of bottles against the wall, and beer taps (a pub) or a neon strip
    const club = variant === 'club',
      smoothie = variant === 'smoothie';
    k.box(w, h - 0.06, d * 0.38, c, 0, (h - 0.06) / 2, d * 0.28, g);
    k.box(w + 0.1, 0.06, d * 0.45, club ? '#111418' : dark(c, 0.55), 0, h - 0.03, d * 0.28, g);
    if (club) k.lit(w, 0.04, 0.02, '#ff2e88', 0, 0.12, d * 0.47 + 0.01, g, 1.6);
    // back bar
    k.box(w, 0.9, d * 0.22, dark(c, 0.75), 0, 0.45, -d * 0.38, g);
    k.box(w, 1.3, 0.06, dark(c, 0.55), 0, 1.55, -d / 2 + 0.03, g);
    for (const y of [1.15, 1.6])
      row(k, Math.round(w * 6), w * 0.92, y, -d / 2 + 0.12, g, smoothie ? FRUIT : BOTTLES, {
        w: 0.07,
        h: 0.26,
        d: 0.07,
      });
    if (club) k.lit(w * 0.9, 0.05, 0.02, '#7b2ff7', 0, 2.15, -d / 2 + 0.07, g, 1.6);
    // taps or blenders on the counter
    const taps = Math.max(2, Math.round(w / 0.6));
    for (let i = 0; i < taps; i += 1) {
      const x = -w * 0.3 + (w * 0.6 * i) / Math.max(1, taps - 1);
      if (smoothie) {
        k.cylinder(0.07, 0.25, FRUIT[i % FRUIT.length]!, x, h + 0.13, d * 0.22, g);
      } else if (!club) {
        k.cylinder(0.02, 0.32, '#c9a227', x, h + 0.16, d * 0.2, g);
        k.box(0.06, 0.1, 0.03, ['#1d4ed8', '#be185d', '#2f6b3f'][i % 3]!, x, h + 0.34, d * 0.2, g);
      } else k.cylinder(0.05, 0.2, BOTTLES[i % BOTTLES.length]!, x, h + 0.1, d * 0.22, g);
    }
    // stools on the customers' side
    for (let i = 0; i < Math.round(w / 0.8); i += 1) {
      const x = -w / 2 + 0.4 + i * 0.8;
      k.cylinder(0.17, 0.05, club ? '#ff2e88' : dark(c, 0.5), x, 0.72, d * 0.62, g);
      k.cylinder(0.03, 0.7, '#3d4650', x, 0.35, d * 0.62, g);
    }
  },
  booth(k, { w, d, h, c }, g) {
    // two padded benches facing over a table
    for (const side of [-1, 1]) {
      const z = side * d * 0.36;
      k.box(w, 0.45, d * 0.26, c, 0, 0.225, z, g);
      k.box(w, h, 0.14, dark(c, 0.8), 0, h / 2, z + side * d * 0.12, g);
    }
    k.box(w * 0.85, 0.05, d * 0.32, '#5b3a29', 0, 0.74, 0, g);
    k.cylinder(0.05, 0.72, '#2a2d31', 0, 0.36, 0, g);
    k.glow(0.04, '#ffd27a', 0.1, 0.8, 0, g);
  },
  coffeebar(k, { w, d, h, c }, g) {
    // a counter with the espresso machine, a glass case of pastries and the menu on the wall
    k.box(w, h - 0.06, d * 0.45, c, 0, (h - 0.06) / 2, d * 0.22, g);
    k.box(w + 0.06, 0.06, d * 0.5, '#e7dcc7', 0, h - 0.03, d * 0.22, g);
    k.box(0.6, 0.45, 0.4, '#c9cfd3', -w * 0.28, h + 0.22, d * 0.18, g); // the machine
    k.box(0.5, 0.08, 0.1, '#2a2d31', -w * 0.28, h + 0.12, d * 0.4, g);
    k.glass(w * 0.32, 0.38, d * 0.4, w * 0.22, h + 0.19, d * 0.22, g); // pastry case
    row(k, 5, w * 0.28, h + 0.03, d * 0.24, g, ['#d9822b', '#c9a227', '#8a4b2b'], { w: 0.1, h: 0.08, d: 0.1 });
    k.box(w, 0.9, d * 0.2, dark(c, 0.7), 0, 0.45, -d * 0.4, g); // back counter
    k.box(w * 0.45, 0.6, 0.04, '#23302a', -w * 0.25, 1.75, -d / 2 + 0.02, g); // the chalk menu
    for (let line = 0; line < 4; line += 1)
      k.box(w * 0.32, 0.025, 0.01, '#e9ecef', -w * 0.25, 1.93 - line * 0.12, -d / 2 + 0.045, g);
  },
  tableset(k, { w, d, h, c, variant }, g) {
    // a table with chairs round it (a café table, a food court, a picnic bench)
    const picnic = variant === 'picnic';
    if (picnic) {
      k.box(w, 0.06, d * 0.45, c, 0, h, 0, g);
      for (const side of [-1, 1]) k.box(w, 0.05, d * 0.18, c, 0, 0.45, side * d * 0.38, g);
      for (const x of [-1, 1]) k.box(0.08, h, d * 0.9, dark(c, 0.7), (x * w) / 2.6, h / 2, 0, g);
      return;
    }
    k.cylinder(Math.min(w, d) * 0.3, 0.05, c, 0, h, 0, g);
    k.cylinder(0.04, h, '#2a2d31', 0, h / 2, 0, g);
    for (const [x, z] of [
      [0, -1],
      [0, 1],
      [-1, 0],
      [1, 0],
    ] as const) {
      const cx = (x * w) / 2.5,
        cz = (z * d) / 2.5;
      k.box(0.38, 0.05, 0.38, dark(c, 0.75), cx, 0.45, cz, g);
      k.cylinder(0.03, 0.43, '#3d4650', cx, 0.215, cz, g);
    }
  },
  kiosk(k, { w, d, h, c, label }, g) {
    // a small booth with a hatch, an awning and its name on top (tickets, merch, gifts, drinks)
    k.box(w, h * 0.8, d, c, 0, (h * 0.8) / 2, 0, g);
    k.box(w * 0.7, h * 0.3, 0.02, '#1f2429', 0, h * 0.5, d / 2 + 0.01, g); // the hatch
    k.box(w * 0.8, 0.05, 0.3, '#e7dcc7', 0, h * 0.36, d / 2 + 0.15, g);
    k.box(w + 0.2, 0.06, d + 0.3, dark(c, 0.7), 0, h * 0.82, 0.1, g);
    k.box(w + 0.2, 0.22, 0.04, light(c, 0.4), 0, h * 0.74, d / 2 + 0.26, g);
    if (label) k.sign(label, 0, h * 0.95, d / 2 - 0.05, g, Math.min(w * 1.1, 2.4), '#fffdf5', dark(c, 0.5));
  },
  foodtruck(k, { w, d, h, c, label }, g) {
    k.box(w, h * 0.75, d, c, 0, h * 0.45, 0, g);
    k.box(w * 0.3, h * 0.35, d * 0.95, light(c, 0.5), w * 0.35, h * 0.62, 0, g); // the cab window
    k.box(w * 0.5, h * 0.28, 0.02, '#1f2429', -w * 0.08, h * 0.55, d / 2 + 0.01, g); // the serving hatch
    k.box(w * 0.55, 0.05, 0.35, '#e7dcc7', -w * 0.08, h * 0.42, d / 2 + 0.17, g);
    for (const x of [-w * 0.32, w * 0.32])
      for (const z of [-1, 1]) k.cylinder(0.18, 0.12, '#1f2429', x, 0.18, (z * d) / 2, g);
    if (label) k.sign(label, -w * 0.08, h * 0.88, d / 2 + 0.01, g, Math.min(w * 0.7, 2), '#fffdf5', dark(c, 0.55));
  },
  fireplace(k, { w, d, h, c }, g) {
    k.box(w, h, d, c, 0, h / 2, 0, g);
    k.box(w * 0.5, h * 0.45, 0.05, '#1a1210', 0, h * 0.25, d / 2, g);
    k.glow(0.1, '#ff8a3d', -0.08, 0.15, d / 2 - 0.05, g);
    k.glow(0.08, '#ffb347', 0.1, 0.12, d / 2 - 0.05, g);
    k.box(w + 0.1, 0.07, d + 0.1, dark(c, 0.7), 0, h + 0.03, 0, g);
    k.box(0.4, 0.5, 0.03, '#c9a227', 0, h + 0.4, -d / 2 + 0.05, g); // a mirror in a gold frame
  },

  // ---------- fun
  dartboard(k, { h }, g) {
    k.box(0.7, 0.9, 0.06, '#1f2429', 0, h, 0, g);
    // the board's rings, face on to the room
    for (const [r, colour, z] of [
      [0.24, '#111418', 0.04],
      [0.2, '#e8e1d0', 0.05],
      [0.13, '#2f6b3f', 0.06],
      [0.06, '#be185d', 0.07],
    ] as const)
      k.cylinder(r, 0.02, colour, 0, h, z, g, r, 16).rotation.x = Math.PI / 2;
  },
  pooltable(k, { w, d, h, c }, g) {
    k.box(w, 0.15, d, '#5b3a29', 0, h - 0.07, 0, g);
    k.box(w * 0.9, 0.02, d * 0.82, c, 0, h + 0.01, 0, g);
    legs(k, w * 0.9, d * 0.82, h - 0.12, '#3d2a1e', g, 0.12);
    row(k, 6, w * 0.4, h + 0.02, 0, g, ['#e9c46a', '#e63946', '#1d4ed8', '#111418', '#2f6b3f', '#fffdf5'], {
      w: 0.06,
      h: 0.06,
      d: 0.06,
    });
    k.box(w * 0.9, 0.03, 0.03, '#c9a227', 0, h + 0.05, d * 0.3, g, 0.15); // a cue
  },
  concertstage(k, { w, d, h, c }, g) {
    // a raised stage with a truss of lights, speaker stacks and a screen behind the band
    k.box(w, h, d, '#1f2429', 0, h / 2, 0, g);
    k.box(w, 0.04, d, c, 0, h + 0.02, 0, g);
    const top = h + 3;
    for (const x of [-1, 1]) {
      k.box(0.18, top, 0.18, '#7d8790', (x * w) / 2, top / 2, d / 2 - 0.2, g);
      k.box(0.7, 1.4, 0.6, '#111418', x * (w / 2 - 0.5), h + 0.7, d / 2 - 0.5, g); // speakers
      k.box(0.7, 0.9, 0.6, '#111418', x * (w / 2 - 0.5), h + 1.85, d / 2 - 0.5, g);
    }
    k.box(w + 0.2, 0.2, 0.2, '#7d8790', 0, top, d / 2 - 0.2, g);
    const lamps = ['#ff2e88', '#00d4ff', '#f59e0b', '#7b2ff7', '#22c55e'];
    for (let i = 0; i < 6; i += 1)
      k.glow(0.1, lamps[i % lamps.length]!, -w / 2 + 0.5 + (i * (w - 1)) / 5, top - 0.18, d / 2 - 0.2, g);
    k.box(w * 0.6, 2, 0.1, '#111418', 0, h + 1.4, -d / 2 + 0.1, g);
    k.lit(w * 0.56, 1.8, 0.02, c, 0, h + 1.4, -d / 2 + 0.16, g, 0.9);
    // the band's kit: a drum riser and mic stands
    k.box(1.4, 0.3, 1, '#2b3238', 0, h + 0.15, -d * 0.2, g);
    k.cylinder(0.3, 0.35, '#e8e1d0', 0, h + 0.47, -d * 0.2, g);
    for (const x of [-0.35, 0, 0.35]) k.cylinder(0.015, 1.4, '#2a2d31', x * w, h + 0.7, d * 0.25, g);
  },
  lanes(k, { w, d, c }, g) {
    // bowling lanes running away from the camera, pins at the far end, the ball return in between
    const lanes = Math.max(1, Math.round(w / 1.3));
    for (let i = 0; i < lanes; i += 1) {
      const x = -w / 2 + (w * (i + 0.5)) / lanes;
      k.box(w / lanes - 0.25, 0.04, d, c, x, 0.02, 0, g);
      for (const side of [-1, 1]) k.box(0.1, 0.06, d, '#2a2d31', x + (side * (w / lanes - 0.15)) / 2, 0.03, 0, g);
      for (const [px, pz] of [
        [0, 0],
        [-0.12, -0.15],
        [0.12, -0.15],
        [-0.24, -0.3],
        [0, -0.3],
        [0.24, -0.3],
      ] as const)
        k.cylinder(0.04, 0.3, '#fffdf5', x + px, 0.19, -d / 2 + 0.6 + pz, g);
      k.sphere(0.11, ['#1d4ed8', '#be185d', '#2f6b3f'][i % 3]!, x + 0.3, 0.15, d / 2 - 0.3, g);
    }
    k.box(w, 1, 0.3, '#111418', 0, 0.5, -d / 2 + 0.15, g);
    k.lit(w * 0.9, 0.25, 0.02, '#00d4ff', 0, 0.85, -d / 2 + 0.31, g, 1);
  },
  bigscreen(k, { w, h, c }, g) {
    // a huge screen on legs (the riverside mega screen, an outdoor cinema)
    const screenH = h * 0.6;
    for (const x of [-1, 1])
      k.box(0.25, h - screenH / 2, 0.25, '#4b5563', (x * w) / 2.6, (h - screenH / 2) / 2, -0.2, g);
    k.box(w + 0.2, screenH + 0.2, 0.3, '#1f2429', 0, h - screenH / 2, -0.1, g);
    k.lit(w, screenH, 0.02, c, 0, h - screenH / 2, 0.06, g, 1.1);
  },
  deckchairs(k, { w, d, c }, g) {
    const chairs = Math.max(1, Math.round(w / 0.8));
    const stripes = [c, '#fffdf5', light(c, 0.3)];
    for (let i = 0; i < chairs; i += 1) {
      const x = -w / 2 + (w * (i + 0.5)) / chairs;
      const seat = k.box(0.55, 0.04, 0.9, stripes[i % stripes.length]!, x, 0.35, 0, g);
      seat.rotation.x = -0.5;
      k.box(0.6, 0.04, 0.04, '#8a5a3b', x, 0.12, d * 0.25, g);
      k.box(0.6, 0.04, 0.04, '#8a5a3b', x, 0.6, -d * 0.3, g);
    }
  },
  dancefloor(k, { w, d }, g) {
    const colours = ['#ff2e88', '#00d4ff', '#f59e0b', '#7b2ff7', '#22c55e', '#e63946'];
    const n = Math.max(2, Math.round(w / 0.75)),
      m = Math.max(2, Math.round(d / 0.75));
    for (let i = 0; i < n; i += 1)
      for (let j = 0; j < m; j += 1)
        k.lit(
          w / n - 0.03,
          0.03,
          d / m - 0.03,
          colours[(i * 2 + j) % colours.length]!,
          -w / 2 + (w * (i + 0.5)) / n,
          0.015,
          -d / 2 + (d * (j + 0.5)) / m,
          g,
          0.55,
        );
  },

  // ---------- shops and markets
  shopfront(k, { w, d, h, c, label }, g) {
    // a shop unit: its walls, a big window with things on show, an open doorway and its name above
    k.box(w, h, 0.15, light(c, 0.75), 0, h / 2, -d / 2 + 0.08, g);
    for (const x of [-1, 1]) k.box(0.15, h, d, light(c, 0.7), (x * (w - 0.15)) / 2, h / 2, 0, g);
    k.box(w, 0.6, d, c, 0, h - 0.3, 0, g); // the fascia
    if (label) k.sign(label, 0, h - 0.3, d / 2 + 0.01, g, Math.min(w * 0.85, 3), '#fffdf5', c);
    k.glass(w * 0.55, h - 0.9, 0.04, -w * 0.18, (h - 0.6) / 2, d / 2 - 0.05, g); // window
    k.box(w * 0.55, 0.3, d * 0.3, dark(c, 0.8), -w * 0.18, 0.15, d / 2 - 0.3, g);
    for (let i = 0; i < 3; i += 1) k.box(0.25, 0.5, 0.2, FRUIT[i + 1]!, -w * 0.34 + i * 0.4, 0.55, d / 2 - 0.3, g);
    k.box(w * 0.8, 0.9, d * 0.25, dark(c, 0.7), 0, 0.45, -d / 2 + 0.3, g); // the shelves inside
  },
  marketstall(k, { w, d, h, c, variant }, g) {
    // a trestle of goods under a striped awning (food, vintage clothes, crafts)
    k.box(w, 0.06, d * 0.6, '#e7dcc7', 0, 0.85, d * 0.1, g);
    legs(k, w, d * 0.6, 0.82, '#5b636b', g, 0.04);
    const goods = variant === 'clothes' ? BOOKS : variant === 'crafts' ? ['#c9a227', '#8a4b2b', '#2b4a8a'] : FRUIT;
    row(k, Math.round(w * 4), w * 0.9, 0.88, d * 0.1, g, goods, { w: 0.18, h: 0.12, d: 0.3 });
    if (variant === 'clothes') {
      k.box(w * 0.9, 0.03, 0.03, '#7d8790', 0, 1.7, -d * 0.35, g);
      row(k, Math.round(w * 4), w * 0.85, 1.05, -d * 0.35, g, BOOKS, { w: 0.18, h: 0.6, d: 0.06 });
    }
    for (const x of [-1, 1]) k.box(0.05, h, 0.05, '#3f3f46', (x * (w - 0.05)) / 2, h / 2, -d / 2 + 0.05, g);
    const stripes = Math.round(w / 0.3);
    for (let i = 0; i < stripes; i += 1)
      k.box(w / stripes, 0.05, d + 0.2, i % 2 ? '#fffdf5' : c, -w / 2 + (w * (i + 0.5)) / stripes, h, 0.05, g);
    k.box(w, 0.2, 0.03, c, 0, h - 0.1, d / 2 + 0.15, g);
  },
  fishstall(k, { w, d, h, c }, g) {
    // a white counter of crushed ice with fish laid out, the price boards over it
    k.box(w, h * 0.9, d, '#e9ecef', 0, (h * 0.9) / 2, 0, g);
    k.box(w * 0.94, 0.06, d * 0.9, '#f4fbff', 0, h * 0.9 + 0.03, 0, g);
    const fish = ['#9aa7b4', '#c97b63', '#e8c4a0', '#6b7c8a'];
    for (let i = 0; i < Math.round(w * 3); i += 1) {
      const x = -w * 0.42 + (w * 0.84 * i) / Math.max(1, Math.round(w * 3) - 1);
      const body = k.box(0.32, 0.06, 0.1, fish[i % fish.length]!, x, h * 0.9 + 0.09, (i % 2 ? 1 : -1) * d * 0.15, g);
      body.rotation.y = 0.4;
    }
    k.box(w, 0.4, 0.04, c, 0, h + 1.1, -d / 2, g);
    for (const x of [-1, 1]) k.box(0.05, h + 1.3, 0.05, '#7d8790', (x * w) / 2, (h + 1.3) / 2, -d / 2, g);
    k.box(w, 0.35, 0.04, '#fffdf5', 0, h + 0.65, -d / 2 + 0.02, g);
  },
  scales(k, { h, c }, g) {
    k.box(0.5, h * 0.7, 0.4, c, 0, (h * 0.7) / 2, 0, g);
    k.box(0.45, 0.05, 0.35, '#c9cfd3', 0, h * 0.72, 0, g);
    k.box(0.3, 0.25, 0.06, '#1f2429', 0, h * 0.95, -0.15, g);
    k.lit(0.24, 0.1, 0.01, '#22c55e', 0, h * 0.95, -0.115, g, 1);
  },
  expobooth(k, { w, d, h, c, label }, g) {
    // an exhibition stand: back wall with its name, a counter, a screen and a banner
    k.box(w, h, 0.1, light(c, 0.85), 0, h / 2, -d / 2 + 0.05, g);
    k.box(0.1, h, d * 0.6, light(c, 0.8), -w / 2 + 0.05, h / 2, -d * 0.2, g);
    k.box(w, 0.5, 0.12, c, 0, h - 0.25, -d / 2 + 0.12, g);
    if (label) k.sign(label, 0, h - 0.25, -d / 2 + 0.19, g, Math.min(w * 0.85, 3), '#fffdf5', c);
    k.box(w * 0.4, 1, 0.5, c, w * 0.15, 0.5, d * 0.15, g);
    k.box(0.9, 0.55, 0.04, '#1f2429', -w * 0.2, 1.5, -d / 2 + 0.12, g);
    k.lit(0.82, 0.48, 0.01, SCREEN, -w * 0.2, 1.5, -d / 2 + 0.145, g, 0.6);
    k.box(0.6, 1.8, 0.03, light(c, 0.3), w / 2 - 0.4, 0.95, d * 0.3, g);
    k.box(0.04, 1.9, 0.04, '#7d8790', w / 2 - 0.4, 0.95, d * 0.3 - 0.04, g);
  },

  // ---------- culture
  pews(k, { w, d, h, c }, g) {
    const rows = Math.max(1, Math.round(d / 0.9));
    for (let r = 0; r < rows; r += 1) {
      const z = -d / 2 + (d * (r + 0.5)) / rows;
      k.box(w, 0.06, 0.4, c, 0, 0.45, z, g);
      k.box(w, h - 0.45, 0.06, dark(c, 0.85), 0, 0.45 + (h - 0.45) / 2, z + 0.2, g);
      for (const x of [-1, 1]) k.box(0.06, h, 0.5, dark(c, 0.7), (x * (w - 0.06)) / 2, h / 2, z, g);
    }
  },
  altar(k, { w, d, h, c }, g) {
    k.box(w + 0.6, 0.15, d + 0.6, '#c7bfb2', 0, 0.075, 0, g); // a step
    k.box(w, h, d, '#e9e2d5', 0, 0.15 + h / 2, 0, g);
    k.box(w + 0.02, h * 0.5, d + 0.02, c, 0, 0.15 + h * 0.62, 0, g); // the altar cloth
    for (const x of [-1, 1]) {
      k.cylinder(0.04, 0.3, '#c9a227', x * w * 0.35, 0.15 + h + 0.15, 0, g);
      k.glow(0.03, '#ffd27a', x * w * 0.35, 0.15 + h + 0.33, 0, g);
    }
    k.box(0.05, 0.5, 0.05, '#c9a227', 0, 0.15 + h + 0.25, 0, g);
    k.box(0.25, 0.05, 0.05, '#c9a227', 0, 0.15 + h + 0.38, 0, g);
  },
  pulpit(k, { h, c }, g) {
    k.cylinder(0.45, h, c, 0, h / 2 + 0.3, 0, g, 0.5, 8);
    k.cylinder(0.12, 0.3, dark(c, 0.7), 0, 0.15, 0, g);
    k.box(0.4, 0.4, 0.3, dark(c, 0.8), 0.5, 0.2, 0.3, g); // the steps
  },
  organ(k, { w, d, h, c }, g) {
    k.box(w, 1, d, c, 0, 0.5, 0, g);
    const pipes = Math.round(w * 6);
    for (let i = 0; i < pipes; i += 1) {
      const x = -w / 2 + (w * (i + 0.5)) / pipes;
      const tall = h - 1 - Math.abs(i - pipes / 2) * 0.12;
      k.cylinder(0.05, tall, '#c9b37a', x, 1 + tall / 2, -d * 0.2, g);
    }
    k.box(w * 0.7, 0.04, 0.25, '#fffdf5', 0, 0.85, d / 2 + 0.05, g);
  },
  candles(k, { w, h }, g) {
    k.box(w, h, 0.35, '#3d2a1e', 0, h / 2, 0, g);
    for (let i = 0; i < Math.round(w * 6); i += 1) {
      const x = -w * 0.42 + (w * 0.84 * i) / Math.max(1, Math.round(w * 6) - 1);
      const z = i % 2 ? -0.08 : 0.08;
      k.cylinder(0.025, 0.12, '#fffdf5', x, h + 0.06, z, g);
      k.glow(0.025, '#ffb347', x, h + 0.15, z, g);
    }
  },
  stainedglass(k, { w, h, c }, g) {
    // a tall arched window of coloured glass, set in the wall
    k.box(w + 0.2, h + 0.2, 0.06, '#c7bfb2', 0, 1 + h / 2, 0, g);
    const panes = ['#1d4ed8', '#be185d', '#c9a227', '#2f6b3f', c];
    for (let i = 0; i < 3; i += 1)
      for (let j = 0; j < 4; j += 1)
        k.lit(
          w / 3 - 0.03,
          h / 4 - 0.03,
          0.02,
          panes[(i + j * 2) % panes.length]!,
          -w / 3 + (w / 3) * i,
          1 + h / 8 + (h / 4) * j,
          0.04,
          g,
          0.5,
        );
  },
  exhibit(k, { w, d, h, c, variant }, g) {
    // a plinth with a glass case and something old inside
    k.box(w, h * 0.6, d, c, 0, (h * 0.6) / 2, 0, g);
    k.glass(w * 0.92, h * 0.4, d * 0.92, 0, h * 0.8, 0, g);
    const inside = variant === 'pot' ? '#b45309' : variant === 'gold' ? '#c9a227' : '#7d8790';
    k.cylinder(Math.min(w, d) * 0.18, h * 0.25, inside, 0, h * 0.72, 0, g, Math.min(w, d) * 0.12);
    k.box(w * 0.5, 0.12, 0.02, '#fffdf5', 0, h * 0.4, d / 2 + 0.01, g);
  },
  shipmodel(k, { w, d, h, c }, g) {
    // a ship in a glass case on a table
    k.box(w, 0.8, d, '#5b3a29', 0, 0.4, 0, g);
    k.box(w * 0.75, 0.25, d * 0.35, c, 0, 0.93, 0, g);
    for (const x of [-0.25, 0, 0.25]) {
      k.cylinder(0.015, h - 1.05, '#3d2a1e', x * w, 1.05 + (h - 1.05) / 2, 0, g);
      k.box(w * 0.18, (h - 1.05) * 0.5, 0.01, '#fffdf5', x * w, 1.05 + (h - 1.05) * 0.55, 0, g);
    }
  },
  barrels(k, { w, d, h, c }, g) {
    const count = Math.max(1, Math.round((w * d) / 0.5));
    for (let i = 0; i < count; i += 1) {
      const x = -w / 2 + 0.3 + ((i * 0.62) % Math.max(0.6, w - 0.3));
      const z = -d / 2 + 0.3 + Math.floor((i * 0.62) / Math.max(0.6, w - 0.3)) * 0.62;
      k.cylinder(0.27, h, c, x, h / 2, z, g, 0.25);
      for (const y of [0.2, 0.8]) k.cylinder(0.28, 0.04, '#3d4650', x, h * y, z, g);
    }
  },
  // ---------- voting (City Hall's polling station)
  votingbooth(k, { w, d, h, c }, g) {
    // booths side by side: a ledge to write on, screens either side and behind, a pencil on a string
    const count = Math.max(1, Math.round(w / 0.9));
    const each = w / count;
    for (let i = 0; i < count; i += 1) {
      const x = -w / 2 + each * (i + 0.5);
      k.box(each * 0.9, 0.04, d * 0.45, light(c, 0.85), x, h * 0.62, -d * 0.2, g); // the ledge
      k.box(each * 0.95, h * 0.45, 0.03, c, x, h * 0.62 + (h * 0.45) / 2, -d * 0.42, g); // the back screen
      k.box(0.05, h * 0.62, 0.05, dark(c, 0.6), x - each * 0.42, (h * 0.62) / 2, -d * 0.35, g);
      k.box(0.05, h * 0.62, 0.05, dark(c, 0.6), x + each * 0.42, (h * 0.62) / 2, -d * 0.35, g);
      k.box(0.08, 0.01, 0.01, '#3d2a1e', x + each * 0.2, h * 0.65, -d * 0.15, g); // the pencil
    }
    for (let i = 0; i <= count; i += 1) k.box(0.03, h * 0.45, d * 0.5, c, -w / 2 + each * i, h * 0.84, -d * 0.2, g);
  },
  ballotbox(k, { w, d, h, c, label }, g) {
    // a black ballot box with its slot, on a table
    k.box(w, 0.05, d, '#d9d4c7', 0, h * 0.55, 0, g);
    legs(k, w, d, h * 0.53, '#5b636b', g);
    k.box(w * 0.55, h * 0.35, d * 0.6, c, 0, h * 0.58 + (h * 0.35) / 2, 0, g);
    k.box(w * 0.3, 0.015, 0.04, '#0b0f14', 0, h * 0.93 + 0.01, 0, g); // the slot
    if (label) k.sign(label, 0, h * 0.75, d * 0.31, g, w * 0.5, '#fffdf5', '#0b0f14');
  },
  dog(k, { c }, g) {
    // a dog sitting, waiting outside
    k.box(0.32, 0.26, 0.42, c, 0, 0.22, 0, g);
    k.box(0.26, 0.3, 0.2, c, 0, 0.42, 0.16, g);
    k.box(0.22, 0.2, 0.24, c, 0, 0.62, 0.24, g);
    k.box(0.1, 0.08, 0.1, dark(c, 0.7), 0, 0.6, 0.38, g); // the nose
    for (const side of [-1, 1]) {
      k.box(0.06, 0.12, 0.04, dark(c, 0.8), side * 0.09, 0.76, 0.2, g); // ears
      k.box(0.07, 0.2, 0.07, c, side * 0.08, 0.1, 0.2, g); // front legs
    }
    k.box(0.05, 0.05, 0.22, c, 0, 0.32, -0.28, g); // the tail
  },
  infoboard(k, { w, h, c, label }, g) {
    k.box(0.06, h * 0.55, 0.06, '#3d4650', 0, (h * 0.55) / 2, 0, g);
    k.box(w, h * 0.45, 0.05, c, 0, h * 0.75, 0, g);
    k.box(w * 0.9, h * 0.38, 0.01, '#fffdf5', 0, h * 0.75, 0.03, g);
    if (label) k.sign(label, 0, h * 0.86, 0.04, g, w * 0.85, '#162e3a', '#fffdf5');
    for (let line = 0; line < 3; line += 1) k.box(w * 0.7, 0.03, 0.01, '#9aa3a8', 0, h * 0.72 - line * 0.08, 0.04, g);
  },
  ropes(k, { w, c }, g) {
    const posts = Math.max(2, Math.round(w / 0.9) + 1);
    for (let i = 0; i < posts; i += 1) {
      const x = -w / 2 + (w * i) / (posts - 1);
      k.cylinder(0.04, 0.9, '#c9a227', x, 0.45, 0, g);
      k.sphere(0.06, '#c9a227', x, 0.93, 0, g);
    }
    k.box(w, 0.05, 0.05, c, 0, 0.75, 0, g);
  },
  telescope(k, { h, c }, g) {
    k.cylinder(0.5, 0.25, '#3d4650', 0, 0.12, 0, g);
    k.cylinder(0.12, h * 0.45, '#5b636b', 0, h * 0.3, 0, g);
    const tube = k.cylinder(0.22, h * 0.8, c, 0, h * 0.62, -0.2, g, 0.18);
    tube.rotation.x = -0.6;
  },
  meridian(k, { w, d, c }, g) {
    // the brass line across a strip of stone, with a plaque
    k.box(w, 0.02, d, '#c7bfb2', 0, 0.01, 0, g);
    k.lit(0.06, 0.025, d, c, 0, 0.02, 0, g, 0.6);
    k.box(0.5, 0.025, 0.3, '#7a5a2a', 0.4, 0.02, d / 2 - 0.3, g);
  },
  planetarium(k, { w, h, c }, g) {
    // a dome of copper-green with a door, and stars inside the doorway
    k.cylinder(w / 2, h * 0.4, '#d9d4c7', 0, h * 0.2, 0, g, w / 2, 16);
    k.dome(w / 2, c, 0, h * 0.4, 0, g);
    k.box(0.9, 1.7, 0.1, '#111827', 0, 0.85, w / 2 - 0.02, g);
    for (const [x, y] of [
      [-0.2, 1.2],
      [0.15, 1.4],
      [0.05, 0.9],
      [-0.25, 0.6],
    ] as const)
      k.glow(0.025, '#fffdf5', x, y, w / 2 + 0.04, g);
  },
  statue(k, { w, h, c }, g) {
    k.box(w, h * 0.45, w, '#c7bfb2', 0, (h * 0.45) / 2, 0, g);
    k.box(w * 1.1, 0.08, w * 1.1, '#a9a196', 0, h * 0.45, 0, g);
    const s = (h * 0.55) / 1.5;
    k.box(0.32 * s, 0.7 * s, 0.2 * s, c, 0, h * 0.45 + 0.85 * s, 0, g);
    k.sphere(0.14 * s, c, 0, h * 0.45 + 1.35 * s, 0, g);
    for (const side of [-1, 1]) k.box(0.12 * s, 0.5 * s, 0.14 * s, c, side * 0.09 * s, h * 0.45 + 0.25 * s, 0, g);
    k.box(0.08 * s, 0.6 * s, 0.08 * s, c, 0.22 * s, h * 0.45 + 1.1 * s, 0.1 * s, g, 0.3);
  },

  // ---------- health and sport
  consult(k, { w, d, h, c }, g) {
    // a curtained booth with an examination bed and a doctor's desk
    k.box(w, h, 0.05, light(c, 0.6), 0, h / 2, -d / 2, g);
    k.box(0.05, h, d, light(c, 0.6), -w / 2, h / 2, 0, g);
    k.box(0.03, 0.03, d, '#9aa3a8', w / 2, h, 0, g); // curtain rail
    k.box(0.02, h * 0.85, d * 0.35, c, w / 2, h * 0.5, -d * 0.3, g); // the curtain, drawn back
    k.box(w * 0.5, 0.15, 0.7, '#e9ecef', -w * 0.18, 0.7, -d * 0.15, g);
    k.box(w * 0.5, 0.06, 0.7, c, -w * 0.18, 0.8, -d * 0.15, g);
    legs(k, w * 0.48, 0.66, 0.62, '#9aa3a8', g, 0.04);
    k.box(0.8, 0.05, 0.5, '#e7dcc7', w * 0.25, 0.75, d * 0.2, g);
    k.box(0.3, 0.45, 0.04, '#1f2429', w * 0.25, 1, d * 0.05, g);
  },
  pharmacy(k, { w, d, h, c, label }, g) {
    k.box(w, 1, d * 0.4, '#e9ecef', 0, 0.5, d * 0.25, g);
    k.box(w + 0.05, 0.05, d * 0.45, c, 0, 1.02, d * 0.25, g);
    k.box(w, h, 0.3, '#f4f6f8', 0, h / 2, -d / 2 + 0.15, g);
    for (const y of [0.6, 1.1, 1.6])
      row(k, Math.round(w * 7), w * 0.9, y, -d / 2 + 0.3, g, ['#fffdf5', '#e63946', '#1d4ed8', '#22c55e'], {
        w: 0.1,
        h: 0.14,
        d: 0.08,
      });
    k.lit(0.3, 0.3, 0.04, '#22c55e', w / 2 - 0.3, h + 0.1, -d / 2 + 0.2, g, 1);
    if (label) k.sign(label, -0.2, h + 0.1, -d / 2 + 0.32, g, Math.min(w * 0.6, 2), '#fffdf5', c);
  },
  weightbench(k, { w, d, c }, g) {
    k.box(0.35, 0.08, d * 0.75, c, 0, 0.45, 0, g);
    legs(k, 0.3, d * 0.7, 0.42, '#3c4349', g, 0.05);
    for (const x of [-1, 1]) k.box(0.06, 1.2, 0.06, '#3c4349', (x * w) / 2.4, 0.6, -d * 0.3, g);
    k.box(w, 0.04, 0.04, '#9aa3a8', 0, 1.05, -d * 0.3, g);
    for (const x of [-1, 1]) {
      const plate = k.cylinder(0.22, 0.06, '#111418', (x * w) / 2.15, 1.05, -d * 0.3, g);
      plate.rotation.z = Math.PI / 2;
    }
    row(k, 6, w * 0.8, 0, d / 2 - 0.1, g, ['#111418', '#3c4349'], { w: 0.12, h: 0.14, d: 0.12 }); // dumbbells
  },
  yogamats(k, { w, d, c }, g) {
    const mats = Math.max(1, Math.round(w / 0.9));
    const colours = [c, '#7b2ff7', '#22c55e', '#f59e0b'];
    for (let i = 0; i < mats; i += 1)
      k.box(0.65, 0.025, d * 0.9, colours[i % colours.length]!, -w / 2 + (w * (i + 0.5)) / mats, 0.0125, 0, g);
  },
  showers(k, { w, d, h, c }, g) {
    // a row of tiled shower stalls with glass fronts
    const stalls = Math.max(1, Math.round(w / 1));
    k.box(w, h, 0.08, c, 0, h / 2, -d / 2 + 0.04, g);
    for (let i = 0; i <= stalls; i += 1) k.box(0.05, h, d, light(c, 0.3), -w / 2 + (w * i) / stalls, h / 2, 0, g);
    for (let i = 0; i < stalls; i += 1) {
      const x = -w / 2 + (w * (i + 0.5)) / stalls;
      k.cylinder(0.09, 0.03, '#c9cfd3', x, h - 0.3, -d / 2 + 0.25, g);
      k.glass(w / stalls - 0.15, h * 0.75, 0.03, x, h * 0.4, d / 2, g);
    }
  },
  mirror(k, { w, h }, g) {
    k.box(w, h, 0.04, '#a9b6bf', 0, 0.3 + h / 2, 0, g);
    k.box(w * 0.96, h * 0.94, 0.01, '#d6e6ee', 0, 0.3 + h / 2, 0.025, g);
  },
};
